import React, { useState, useRef, useEffect, useContext } from "react";
import "./ChatPage.css";
import Navbar from "../../components/Navbar/Navbar";
import ChatInput from "../../components/ChatInput/ChatInput";
import MessageBubble from "../../components/MessageBubble/MessageBubble";
import Carousel from "../../components/Carousel/Carousel";
import Modal from "../../components/Modal/Modal";
import { chatWithAI, fetchNewMessages } from "../../services/api";  // Removed unused storeUserIdentification
import '@fortawesome/fontawesome-free/css/all.min.css';
import TransparentBox from '../../components/TransparentBox/TransparentBox';
import CartPanel from '../../components/CartPanel/CartPanel';
import SuggestionButtons from '../../components/SuggestionButtons/SuggestionButtons';
import { CartContext } from '../../context/CartContext';
import Cookies from 'js-cookie';
import { useLocation, useParams, useNavigate } from "react-router-dom";

function ChatPage() {
    const [messages, setMessages] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isAiTyping, setIsAiTyping] = useState(false);
    const [, setForceUpdate] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const chatEndRef = useRef(null);
    const chatboxRef = useRef(null);
    const inputContainerRef = useRef(null);
    const suggestionsContainerRef = useRef(null);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const { cartItems, addToCart, removeFromCart } = useContext(CartContext);
    const [modalImages, setModalImages] = useState([]);
    const [modalImageIdx, setModalImageIdx] = useState(0);
    const [modalItem, setModalItem] = useState(null);
    const location = useLocation();
    const { chatId } = useParams();
    const navigate = useNavigate();
    const typingTimeoutRef = useRef(null);
    const typingStartTimeRef = useRef(null);
    const lastAiMessageTimeRef = useRef(null);
    const waitingForResponseRef = useRef(false);
    const lastUserMessageTimeRef = useRef(null);

    const toggleDropdown = () => {
        if (!isDropdownOpen) {
            handleDropdownOpen();
        } else {
            setIsDropdownOpen(false);
        }
    };

    const handleNewMessages = (newMessages) => {
        console.log('ChatPage received new messages:', newMessages);
        if (!newMessages || newMessages.length === 0) {
            return;
        }
        
        let hasNewAIMessage = false;
        
        setMessages(prevMessages => {
            const allMessages = [...prevMessages];
            newMessages.forEach(msg => {
                console.log('Processing message in ChatPage:', msg);
                
                // More robust deduplication - check by content and sender, not just timestamp
                const messageContent = msg.message || msg.text || '';
                const existingMsgIndex = allMessages.findIndex(existing => {
                    const existingContent = existing.message || existing.text || '';
                    return existing.sender === msg.sender && 
                           existingContent === messageContent &&
                           Math.abs(existing.timestamp - msg.timestamp) < 10000; // Within 10 seconds
                });
                
                console.log('Existing message index:', existingMsgIndex);

                if (existingMsgIndex === -1) {
                    allMessages.push(msg);
                    // ONLY count AI messages for typing indicator logic
                    // Check for various AI sender formats
                    const isAIMessage = msg.sender === "AI" || msg.sender === "ADMIN" || msg.sender === "ai" || 
                                       msg.sender === "admin" || msg.sender === "ASSISTANT" || msg.sender === "assistant";
                    
                    console.log(`Message sender: "${msg.sender}", isAI: ${isAIMessage}, type: ${msg.type}`);
                    
                    if (isAIMessage) {
                        hasNewAIMessage = true;
                        lastAiMessageTimeRef.current = Date.now();
                        console.log('New AI message detected, will update typing indicator');
                        
                        // Check if this AI message is a response to the current user message
                        if (waitingForResponseRef.current && lastUserMessageTimeRef.current) {
                            const timeSinceUserMessage = msg.timestamp - lastUserMessageTimeRef.current;
                            // If AI message is within 30 seconds of user message, it's likely a response
                            if (timeSinceUserMessage > 0 && timeSinceUserMessage < 30000) {
                                console.log('AI response detected for current user message (within 30s), stopping typing indicator');
                                waitingForResponseRef.current = false;
                            } else if (timeSinceUserMessage > 0 && timeSinceUserMessage < 120000) {
                                // If AI message is within 2 minutes, it might be a slow response
                                console.log('AI response detected for current user message (slow response, within 2min), stopping typing indicator');
                                waitingForResponseRef.current = false;
                            }
                        }
                    } else {
                        console.log(`Ignoring ${msg.sender} message from polling for typing logic`);
                    }
                } else {
                    console.log('Duplicate message ignored');
                }
            });
            const sortedMessages = allMessages.sort((a, b) => a.timestamp - b.timestamp);
            console.log('Final sorted messages in ChatPage:', sortedMessages);
            return sortedMessages;
        });

        // Simple typing indicator logic - stop immediately when AI message appears
        if (hasNewAIMessage) {
            console.log('AI message detected, stopping typing indicator immediately');
            
            // Clear the fallback timeout
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = null;
            }
            
            setIsAiTyping(false);
            waitingForResponseRef.current = false;
            // Force immediate re-render
            setForceUpdate(prev => prev + 1);
        }
    };

    // Update URL with chat ID if not already there and we have a chat ID
    const currentChatId = Cookies.get('chat_id');
    if (currentChatId && !chatId) {
        console.log(`Updating URL to include chat ID: ${currentChatId}`);
        navigate(`/chat/${currentChatId}`, { replace: true });
    }

    const handleSendMessage = async (e) => {
        e.preventDefault();
        const input = e.target.elements.messageInput.value.trim();
        if (!input) return;

        const userMessage = {
            sender: "USER",
            text: input,
            timestamp: Date.now(),
            type: "text"
        };
        setMessages(prev => [...prev, userMessage]);
        e.target.reset();


        
        setIsAiTyping(true);
        typingStartTimeRef.current = Date.now();
        lastAiMessageTimeRef.current = null;
        waitingForResponseRef.current = true;
        lastUserMessageTimeRef.current = Date.now();
        
        // Add a fallback timeout for very slow responses (2 minutes)
        typingTimeoutRef.current = setTimeout(() => {
            console.log('Fallback timeout: stopping typing after 2 minutes (very slow response)');
            setIsAiTyping(false);
            waitingForResponseRef.current = false;
            typingTimeoutRef.current = null;
        }, 120000); // 2 minutes
        
        try {
            const result = await chatWithAI(input, handleNewMessages);
            
            // If we got an immediate response, stop the typing indicator immediately
            if (result && result.isImmediateResponse) {
                console.log('Immediate response received, stopping typing indicator');
                setIsAiTyping(false);
                waitingForResponseRef.current = false;
                
                // Clear the fallback timeout
                if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                    typingTimeoutRef.current = null;
                }
            }
            
            // If we're polling (status 202 or payment link), keep typing indicator active
            if (result && result.isPolling) {
                console.log('Polling started - keeping typing indicator active');
                // Don't stop typing indicator, let it continue until polling completes
                // The fallback timeout will still work as a safety measure
            }
        } catch (error) {
            console.error("Chat error:", error);
            setMessages(prev => [...prev, {
                sender: "AI",
                text: "Sorry, something went wrong. Please try again later.",
                timestamp: Date.now(),
                type: "text"
            }]);
            // Stop typing on error
            setIsAiTyping(false);
            waitingForResponseRef.current = false;
        }
    };

    useEffect(() => {
        // Only scroll if there are messages and the last message is new (within last 5 seconds)
        if (messages.length > 0) {
            const lastMessage = messages[messages.length - 1];
            const isRecentMessage = Date.now() - lastMessage.timestamp < 5000;
            if (isRecentMessage) {
                // Add a small delay to prevent interference with animations
                setTimeout(() => {
                    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
                }, 100);
            }
        }
    }, [messages]);





    // Handle URL chatId parameter and load chat data
    useEffect(() => {
        const currentChatId = Cookies.get('chat_id');
        
        if (chatId) {
            console.log(`Chat ID found in URL: ${chatId}`);
            
            // Note: Cookie management is handled by the API service
            // No need to set cookies here as fetchNewMessages will handle it
            
            // DO NOT interfere with user_id or device_id cookies
            // Let the server handle authentication and review access
            console.log('Current cookies:', {
                user_id: Cookies.get('user_id'),
                session_id: Cookies.get('session_id'),
                device_id: Cookies.get('device_id'),
                chat_id: Cookies.get('chat_id')
            });
            
            // Clear timestamp to ensure full chat history load for this chat
            Cookies.remove('last_synced_server_timestamp');
            console.log('Cleared last_synced_server_timestamp for fresh chat load');
            
            // Immediately load chat data for this specific chatId
            const loadChatData = async () => {
                try {
                    // Clear existing messages first
                    setMessages([]);
                    
                    // Load from localStorage for this specific chat
                    const storageKey = `chat_messages_${chatId}`;
                    const savedMessages = localStorage.getItem(storageKey);
                    
                    if (savedMessages) {
                        const parsedMessages = JSON.parse(savedMessages);
                        
                        // Debug: Check if carousel messages have compare_at_price data
                        const carouselMessages = parsedMessages.filter(msg => msg.type === 'carousel');
                        if (carouselMessages.length > 0) {
                            console.log('Carousel messages loaded from localStorage:', carouselMessages.map(msg => ({
                                type: msg.type,
                                itemsCount: msg.items?.length,
                                itemsWithComparePrice: msg.items?.map(item => ({
                                    title: item.title,
                                    price: item.price,
                                    compare_at_price: item.compare_at_price
                                }))
                            })));
                        }
                        
                        setMessages(parsedMessages);
                        console.log(`Loaded ${parsedMessages.length} messages from localStorage for chat: ${chatId}`);
                        // Scroll to last message after a short delay to ensure DOM is ready
                        setTimeout(() => {
                            chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
                        }, 100);
                    }
                    
                    // Always fetch from backend for this specific chat
                    console.log(`Fetching messages for chat ID: ${chatId}`);
                    const data = await fetchNewMessages(chatId, handleNewMessages);
                    console.log(`Loaded chat data for ${chatId}:`, data);
                    
                    if (!data.synced_messages || data.synced_messages.length === 0) {
                        console.warn('No messages returned from backend. This could be due to user identification mismatch.');
                        console.log('Chat URL suggests this is an existing chat, but backend returned empty results.');
                    }
                    
                    // Scroll to bottom after backend data loads
                    setTimeout(() => {
                        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
                    }, 200);
                    
                } catch (error) {
                    console.error(`Error loading chat ${chatId}:`, error);
                }
            };
            
            loadChatData();
            
        } else if (currentChatId) {
            // No chatId in URL but we have a current chat - redirect to proper URL
            console.log(`Redirecting to /chat/${currentChatId}`);
            navigate(`/chat/${currentChatId}`, { replace: true });
            return;
        }
        // If no chatId in URL and no current chat, stay on /chat (new chat)
    }, [chatId, navigate]);

    useEffect(() => {
        // Only load general chat data if no specific chatId in URL
        // (URL-specific loading is handled in the chatId useEffect above)
        if (!chatId) {
            console.log('No chatId in URL - loading general chat data');
            
            // Don't load from localStorage if there's an initial message to avoid conflicts
            if (location.state?.initialMessage) {
                console.log('Initial message present, skipping localStorage loading to avoid conflicts');
                return;
            }
            
            // Load from localStorage first (general chat)
            const currentChatId = Cookies.get('chat_id');
            const storageKey = currentChatId ? `chat_messages_${currentChatId}` : 'chat_messages';
            const savedMessages = localStorage.getItem(storageKey);
            
            if (savedMessages) {
                const parsedMessages = JSON.parse(savedMessages);
                setMessages(parsedMessages);
                console.log(`Loaded ${parsedMessages.length} messages from localStorage for chat: ${currentChatId || 'new'}`);
                // Scroll to last message after a short delay to ensure DOM is ready
                setTimeout(() => {
                    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
                }, 100);
            } else {
                // Clear messages if no saved data for this chat
                setMessages([]);
                console.log(`No saved messages found for chat: ${currentChatId || 'new'}`);
            }

            // Always fetch from backend to get latest messages
            const fetchInitialMessages = async () => {
                try {
                    const currentChatId = Cookies.get('chat_id');
                    if (currentChatId) {
                        const data = await fetchNewMessages(currentChatId, handleNewMessages);
                        console.log('Initial messages loaded:', data);
                    }
                } catch (error) {
                    console.error('Error fetching initial messages:', error);
                }
            };
            fetchInitialMessages();
        }

        // Handle browser back/forward navigation
        const handlePopState = () => {
            // Close any open modals or dropdowns when navigating back/forward
            setIsModalOpen(false);
            setIsDropdownOpen(false);
            setIsCartOpen(false);
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
            // Cleanup typing timeout on unmount
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        };
    }, [chatId, location.state]); // Add chatId and location.state dependencies

    useEffect(() => {
        // Auto-send initialMessage if present in location.state
        if (location.state?.initialMessage) {
            const userMessage = {
                sender: "USER",
                text: location.state.initialMessage,
                timestamp: Date.now(),
                type: "text"
            };
            setMessages(prev => [...prev, userMessage]);
            setIsAiTyping(true);
            waitingForResponseRef.current = true;
            lastUserMessageTimeRef.current = Date.now();
            
            // Safety timeout for initial message
            typingTimeoutRef.current = setTimeout(() => {
                console.log('Safety timeout: stopping typing after 10 seconds (initial message)');
                setIsAiTyping(false);
                waitingForResponseRef.current = false;
                typingTimeoutRef.current = null;
            }, 10000);
            
            chatWithAI(location.state.initialMessage, handleNewMessages)
                .then((result) => {
                    // If we got an immediate response, stop the typing indicator immediately
                    if (result && result.isImmediateResponse) {
                        console.log('Immediate response received for initial message, stopping typing indicator');
                        setIsAiTyping(false);
                        waitingForResponseRef.current = false;
                        
                        // Clear the safety timeout
                        if (typingTimeoutRef.current) {
                            clearTimeout(typingTimeoutRef.current);
                            typingTimeoutRef.current = null;
                        }
                    }
                })
                .catch(() => {
                    setMessages(prev => [
                        ...prev,
                        {
                            sender: "AI",
                            text: "Sorry, I couldn't connect to the server. Please try again later.",
                            timestamp: Date.now(),
                            type: "text"
                        }
                    ]);
                    // Only stop typing on actual error
                    if (typingTimeoutRef.current) {
                        clearTimeout(typingTimeoutRef.current);
                        typingTimeoutRef.current = null;
                    }
                    setIsAiTyping(false);
                    waitingForResponseRef.current = false;
                });
            // Clear the initialMessage from history state so it doesn't resend on back/forward
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    // Save messages to localStorage whenever they change (specific to this chat)
    useEffect(() => {
        const currentChatId = Cookies.get('chat_id');
        const storageKey = currentChatId ? `chat_messages_${currentChatId}` : 'chat_messages';
        localStorage.setItem(storageKey, JSON.stringify(messages));
        console.log(`Saved ${messages.length} messages to localStorage for chat: ${currentChatId || 'new'}`);
    }, [messages]);

    // Format time only (no date)
    const formatTime = (timestamp) => {
        const dateObj = new Date(timestamp);
        return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Group messages by date and render a day separator at the top
    const renderMessagesWithDateSeparator = (messages) => {
        if (!messages.length) return null;
        let lastDate = null;
        return messages.map((msg, idx) => {
            const msgDateObj = new Date(msg.timestamp);
            // Use first three letters for day and month, and only day of month
            const dayOfWeek = msgDateObj.toLocaleDateString('en-US', { weekday: 'short' });
            const month = msgDateObj.toLocaleDateString('en-US', { month: 'short' });
            const day = msgDateObj.getDate();
            const msgDate = `${dayOfWeek}, ${month} ${day}`;
            const showDate = msgDate !== lastDate;
            lastDate = msgDate;
            return (
                <React.Fragment key={idx}>
                    {showDate && (
                        <div className="day-separator">
                            <span>{msgDate}</span>
                        </div>
                    )}
                    <div className="message-group">
                        {renderMessage(msg, idx === 0)}
                    </div>
                </React.Fragment>
            );
        });
    };

    const handleCartClick = () => {
        handleCartOpen();
    };

    const handleRemoveFromCart = (itemId) => {
        removeFromCart(itemId);
    };

    const handleAction = (e, action, item) => {
        if (e && e.stopPropagation) e.stopPropagation();
        if (action === 'buy') {
            // Send a user message to the chat: I want to buy <title> (Size: <size>)
            const userMessage = `I want to buy ${item.title}${item.size ? ` (Size: ${item.size})` : ''}`;
            setMessages(prev => [
                ...prev,
                {
                    sender: 'USER',
                    text: userMessage,
                    timestamp: Date.now(),
                    type: 'text'
                }
            ]);
            setIsAiTyping(true);
            waitingForResponseRef.current = true;
            lastUserMessageTimeRef.current = Date.now();
            
            chatWithAI(userMessage, handleNewMessages)
                .then((result) => {
                    // If we got an immediate response, stop the typing indicator immediately
                    if (result && result.isImmediateResponse) {
                        console.log('Immediate response received for carousel action, stopping typing indicator');
                        setIsAiTyping(false);
                        waitingForResponseRef.current = false;
                    }
                })
                .catch(error => {
                    console.error('Chat error:', error);
                    setIsAiTyping(false);
                    waitingForResponseRef.current = false;
                });
            return;
        }
        if (action === 'cart') {
            console.log('Adding item to cart:', {
                id: item.id,
                title: item.title,
                price: item.price,
                fullItem: item
            });
            addToCart(item);
            triggerCartAnimation();
        }
        console.log(`${action} clicked`);
    };

    const handleTransition = (item) => {
        let images = item.allImages && item.allImages.length > 0 ? item.allImages : item.productUrl ? [item.productUrl] : [];
        setModalImages(images);
        setModalImageIdx(0);
        setModalItem(item);
        handleModalOpen();
    };

    const handleModalPrev = () => {
        setModalImageIdx(idx => (idx > 0 ? idx - 1 : modalImages.length - 1));
    };
    const handleModalNext = () => {
        setModalImageIdx(idx => (idx < modalImages.length - 1 ? idx + 1 : 0));
    };
    const handleModalBuy = () => {
        if (modalItem) {
            const userMessage = `I want to buy ${modalItem.title}`;
            setMessages(prev => [
                ...prev,
                {
                    sender: 'USER',
                    text: userMessage,
                    timestamp: Date.now(),
                    type: 'text'
                }
            ]);
            setIsAiTyping(true);
            waitingForResponseRef.current = true;
            lastUserMessageTimeRef.current = Date.now();
            
            chatWithAI(userMessage, handleNewMessages)
                .then((result) => {
                    // If we got an immediate response, stop the typing indicator immediately
                    if (result && result.isImmediateResponse) {
                        console.log('Immediate response received for modal buy, stopping typing indicator');
                        setIsAiTyping(false);
                        waitingForResponseRef.current = false;
                    }
                })
                .catch(error => {
                    console.error('Chat error:', error);
                    setIsAiTyping(false);
                    waitingForResponseRef.current = false;
                });
            setIsModalOpen(false);
        }
    };

    const triggerCartAnimation = () => {
        setIsAnimating(true);
        setTimeout(() => {
            setIsAnimating(false);
        }, 1000);
    };

    const suggestions = [
        "ℹ️ What's your return policy?",
        "❓ What can you do?",
        "📖 How to place an order?",
        "🎁 Surprise me with a pair",
        "👟 How do I know my shoe size?"
    ];

    const handleSuggestionClick = (suggestion) => {
        // Create and send the message directly
        const userMessage = {
            sender: "USER",
            text: suggestion,
            timestamp: Date.now(),
            type: "text"
        };
        setMessages(prev => [...prev, userMessage]);


        
        setIsAiTyping(true);
        typingStartTimeRef.current = Date.now();
        lastAiMessageTimeRef.current = null;
        waitingForResponseRef.current = true;
        lastUserMessageTimeRef.current = Date.now();
        
        // No safety timeouts - let AI response handle it
        
        chatWithAI(suggestion, handleNewMessages)
            .then((result) => {
                // If we got an immediate response, stop the typing indicator immediately
                if (result && result.isImmediateResponse) {
                    console.log('Immediate response received for suggestion, stopping typing indicator');
                    setIsAiTyping(false);
                    waitingForResponseRef.current = false;
                }
            })
            .catch(error => {
                console.error('Chat error:', error);
                setIsAiTyping(false);
                waitingForResponseRef.current = false;
            });
    };

    const renderMessage = (message, isFirstMessage = false) => {
        console.log('Rendering message:', message, 'isFirstMessage:', isFirstMessage);
        const isAI = message.sender === "AI";
        const messageClass = isAI ? "message-bubble ai" : "message-bubble user";

        switch (message.type) {
            case "text":
            case "prompt":
                return (
                    <MessageBubble
                        message={message.message || message.text}
                        timestamp={formatTime(new Date(message.timestamp))}
                        sender={message.sender}
                        type={message.type}
                        isFirstMessage={isFirstMessage}
                    />
                );
            case "carousel":
                // --- AI carousel bubble with avatar and label ---
                if (message.sender === "AI") {
                    return (
                        <div className="message-bubble-row ai">
                            <div className="message-avatar">
                                <img src="/logo.png" alt="Ria Logo" />
                            </div>
                            <div className="message-bubble ai carousel-bubble">
                                <span className="bubble-sender-label">Ria</span>
                                <div className="carousel-scroll-container">
                                    <Carousel
                                        items={message.items.map(item => {
                                            // Use pre-processed allImages from API, or fall back to processing
                                            let allImages = item.allImages || [];
                                            
                                            // Fallback processing if allImages is empty
                                            if (allImages.length === 0) {
                                                const urlString = item.product_url || item.productUrl || '';
                                                if (urlString) {
                                                    allImages = urlString.split(',')
                                                        .map(s => s.replace(/^%20+/, '').trim())
                                                        .filter(s => s && (s.startsWith('http://') || s.startsWith('https://')));
                                                }
                                            }
                                            
                                            return {
                                                ...item,
                                                allImages,
                                                productUrl: item.productUrl || allImages[0] || '',
                                                actions: [{ url: allImages[0] || item.productUrl || item.product_url || '' }]
                                            };
                                        })}
                                        onItemClick={handleTransition}
                                        onAction={handleAction}
                                    />
                                </div>
                                <div className="message-timestamp">
                                    {formatTime(new Date(message.timestamp))}
                                </div>
                            </div>
                        </div>
                    );
                }
                // --- fallback for non-AI carousel (shouldn't happen) ---
                return (
                    <div className={messageClass}>
                        <div className="carousel-scroll-container">
                            <Carousel
                                items={message.items}
                                onItemClick={handleTransition}
                                onAction={handleAction}
                            />
                        </div>
                        <div className="message-timestamp">
                            {formatTime(new Date(message.timestamp))}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    // Deduplicate messages before rendering
    const getDedupedMessages = (messages) => {
        const deduped = [];
        for (let i = 0; i < messages.length; i++) {
            const msg = messages[i];
            
            // For carousel messages, preserve the full structure including compare_at_price
            if (msg.type === 'carousel' && msg.items && Array.isArray(msg.items)) {
                // Check if we already have a carousel with the same items
                const isDuplicateCarousel = deduped.some(existing =>
                    existing.type === 'carousel' &&
                    existing.sender === msg.sender &&
                    existing.items && Array.isArray(existing.items) &&
                    existing.items.length === msg.items.length &&
                    existing.items.every((item, index) => 
                        item.id === msg.items[index]?.id &&
                        item.title === msg.items[index]?.title
                    )
                );
                
                if (!isDuplicateCarousel) {
                    // Preserve the full carousel structure with compare_at_price
                    const processedCarousel = {
                        ...msg,
                        items: msg.items.map(item => ({
                            ...item,
                            compare_at_price: item.compare_at_price // Ensure this field is preserved
                        }))
                    };
                    
                    console.log('Preserving carousel with compare_at_price data:', processedCarousel.items.map(item => ({
                        title: item.title,
                        price: item.price,
                        compare_at_price: item.compare_at_price
                    })));
                    
                    deduped.push(processedCarousel);
                }
                continue;
            }
            
            // For user messages, dedupe if same content and within 5 seconds (API echo)
            const isUser = msg.sender === 'USER';
            const isDuplicate = deduped.some(existing =>
                existing.sender === msg.sender &&
                (existing.message || existing.text) === (msg.message || msg.text) &&
                Math.abs(existing.timestamp - msg.timestamp) < (isUser ? 5000 : 1000)
            );
            // Also dedupe if a USER message matches an AI/system echo (same content, within 5s)
            const isEchoDuplicate = isUser && deduped.some(existing =>
                existing.sender !== 'USER' &&
                (existing.message || existing.text) === (msg.message || msg.text) &&
                Math.abs(existing.timestamp - msg.timestamp) < 5000
            );
            if (!isDuplicate && !isEchoDuplicate) {
                deduped.push(msg);
            }
        }
        return deduped;
    };

    // --- AGGREGATE SHOE RECOMMENDATIONS INTO INDIVIDUAL CAROUSELS ---
    const getDisplayMessages = (messages) => {
        const deduped = getDedupedMessages(messages);
        const shoeRecs = [];
        let explanationMsg = null;
        const otherMsgs = [];

        // Identify and extract shoe recs and explanation
        for (let i = 0; i < deduped.length; i++) {
            const msg = deduped[i];
            
            // Handle already processed carousel messages (from localStorage)
            if (msg.type === 'carousel' && msg.items && Array.isArray(msg.items)) {
                // Ensure all items have compare_at_price preserved
                const processedItems = msg.items.map(item => ({
                    ...item,
                    compare_at_price: item.compare_at_price // Ensure this field is preserved
                }));
                
                console.log('Processing carousel from localStorage, items with compare_at_price:', processedItems.map(item => ({
                    title: item.title,
                    price: item.price,
                    compare_at_price: item.compare_at_price
                })));
                
                otherMsgs.push({
                    ...msg,
                    items: processedItems
                });
                continue;
            }
            
            // Handle raw ShoeRecommendationEntity messages (from API)
            if (msg.type === 'ShoeRecommendationEntity' && msg.value) {
                // Handle both image_ids array and product_url string
                let imageArr = [];
                if (msg.value.image_ids && Array.isArray(msg.value.image_ids)) {
                    imageArr = msg.value.image_ids.filter(url => url && url.startsWith('http'));
                } else if (msg.value.product_url) {
                    imageArr = msg.value.product_url.split(',').map(s => s.trim()).filter(Boolean);
                }
                
                shoeRecs.push({
                    id: msg.value.shoe_id || msg.value.id || i,
                    title: msg.value.brand || msg.value.name,
                    description: msg.value.description,
                    price: msg.value.price,
                    compare_at_price: msg.value.compare_at_price,
                    productUrl: imageArr[0] || '',
                    allImages: imageArr,
                    actions: [{ url: imageArr[0] || '' }],
                    ...msg.value
                });
            } else if ((msg.type === 'RecommendationExplanationOutput' || msg.type === 'recommendation_explanation') && !explanationMsg) {
                explanationMsg = msg;
            } else {
                otherMsgs.push(msg);
            }
        }

        // Insert a single carousel message with all shoe recommendations (only if we found raw ShoeRecommendationEntity messages)
        if (shoeRecs.length > 0) {
            // Find insertion index: after last user message before first shoe rec
            let insertIdx = otherMsgs.findIndex(m => m.timestamp > (deduped.find(m2 => m2.type === 'ShoeRecommendationEntity')?.timestamp || 0));
            if (insertIdx === -1) insertIdx = otherMsgs.length;
            
            // Create a single carousel message containing all shoes
            const carouselMsg = {
                type: 'carousel',
                sender: 'AI',
                items: shoeRecs, // Pass all shoe recommendations as items
                timestamp: Date.now()
            };
            
            // Insert carousel and explanation
            otherMsgs.splice(insertIdx, 0, carouselMsg);
            if (explanationMsg) {
                otherMsgs.splice(insertIdx + 1, 0, explanationMsg);
            }
        }
        return otherMsgs;
    };

    // Dynamic padding-bottom for .chatbox
    useEffect(() => {
        function updateChatboxPadding() {
            if (!chatboxRef.current) return;
            let inputHeight = inputContainerRef.current?.offsetHeight || 0;
            let suggestionsHeight = suggestionsContainerRef.current?.offsetHeight || 0;
            chatboxRef.current.style.paddingBottom = Math.max(0, inputHeight + suggestionsHeight - 100) + 'px';
        }
        updateChatboxPadding();
        window.addEventListener('resize', updateChatboxPadding);
        return () => window.removeEventListener('resize', updateChatboxPadding);
    }, [messages, isDropdownOpen, isAiTyping]);

    // --- Fix: Set root height to window.innerHeight in pixels for perfect iframe/mobile alignment ---
    useEffect(() => {
        function setRootHeight() {
            const h = window.innerHeight;
            document.documentElement.style.height = h + 'px';
            document.body.style.height = h + 'px';
            const root = document.getElementById('root');
            if (root) root.style.height = h + 'px';
        }
        setRootHeight();
        window.addEventListener('resize', setRootHeight);
        return () => window.removeEventListener('resize', setRootHeight);
    }, []);

    // Add history entry when opening modals or dropdowns
    const handleModalOpen = () => {
        setIsModalOpen(true);
        window.history.pushState({ modal: true }, '');
    };

    const handleDropdownOpen = () => {
        setIsDropdownOpen(true);
        window.history.pushState({ dropdown: true }, '');
    };

    const handleCartOpen = () => {
        setIsCartOpen(true);
        window.history.pushState({ cart: true }, '');
    };

    const handleNewChat = () => {
        // Clear all chat-related localStorage
        const currentChatId = Cookies.get('chat_id');
        if (currentChatId) {
            localStorage.removeItem(`chat_messages_${currentChatId}`);
        }
        localStorage.removeItem('chat_messages');
        
        // Clear all chat-related cookies
        Cookies.remove('chat_id');
        Cookies.remove('last_synced_server_timestamp');
        
        // Generate new session ID for fresh start
        const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        Cookies.set('session_id', newSessionId, { expires: 1 }); // 1 day expiry to match other cookies
        
        console.log('New chat started - cleared localStorage and cookies, new session:', newSessionId);
        
        // Navigate to fresh chat page
        navigate('/chat');
        
        // Reload the page to ensure clean state
        window.location.reload();
    };

    return (
        <div className={`chatlive-container`}>
            <Navbar onCartClick={handleCartClick} className={isModalOpen ? 'modal-open' : ''} />
            <div className={`dropdown-bar ${isDropdownOpen ? 'open' : ''}`} onClick={toggleDropdown}>
                <div className="dropdown-text">
                    Swipe to explore styles
                    <img src="/dropdown-arrow.png" alt="Dropdown" className="dropdown-icon" />
                </div>
            </div>

            <div 
                className={`dropdown-overlay ${isDropdownOpen ? "open" : ""}`}
                onClick={toggleDropdown}
            />

            <div className={`dropdown-content ${isDropdownOpen ? "open" : ""}`}>
                <div className="transparent-box-container">
                    <TransparentBox 
                    />
                </div>

                <div className="back-to-chat-container">
                    <button className="back-to-chat-btn" onClick={toggleDropdown}>
                        <div className="back-icon-dropdown">
                            <img src="/arrow-sm-up.png" alt="Back to chat" />
                        </div>
                    </button>
                    <span className="back-text">Back to chat</span>
                </div>
            </div>

            <div className={`chatbox ${isModalOpen ? 'modal-open' : ''}`} ref={chatboxRef}>
                <div className="chat-messages">
                    {renderMessagesWithDateSeparator(getDisplayMessages(messages))}
                    {isAiTyping && waitingForResponseRef.current && (
                        <div className="message-group">
                            <div className="typing-bubble-container">
                                <div className="message-avatar">
                                    <img src="/logo.png" alt="Ria Logo" />
                                </div>
                                <div className="typing-indicator">
                                    <span className="typing-dot"></span>
                                    <span className="typing-dot"></span>
                                    <span className="typing-dot"></span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={chatEndRef}></div>
                    <div className="chat-footer">
                        <button 
                            className="new-chat-button-bottom"
                            onClick={handleNewChat}
                            title="Start New Chat"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                            New Chat
                        </button>
                        <div className="powered-by-text">
                            Powered by Agently
                        </div>
                    </div>
                </div>

                <div className={`chat-input-container ${isDropdownOpen ? 'dropdown-open' : ''} ${isModalOpen ? 'modal-open' : ''}`} ref={inputContainerRef}>
                    <div ref={suggestionsContainerRef}>
                        <SuggestionButtons 
                            suggestions={suggestions}
                            onSuggestionClick={handleSuggestionClick}
                        />
                    </div>
                    <ChatInput
                        onSubmit={handleSendMessage}
                        placeholder="Talk to your AI friend to find the best fit..."
                    />
                </div>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            >
                <div className="modal-image-container">
                    <button className="carousel-img-nav left" onClick={handleModalPrev} aria-label="Previous image">&#8592;</button>
                    <img
                        src={modalImages[modalImageIdx]}
                        alt={modalItem?.title}
                        className="modal-image"
                        style={{ maxWidth: '100%', maxHeight: '320px', objectFit: 'contain' }}
                    />
                    <button className="carousel-img-nav right" onClick={handleModalNext} aria-label="Next image">&#8594;</button>
                </div>
                <div className="modal-info">
                    <div className="modal-title">{modalItem?.title}</div>
                    {modalItem?.price && (
                        <div className="modal-price">Rs. {modalItem.price}</div>
                    )}
                </div>
                <div className="modal-actions">
                    <button className="modal-button primary" onClick={handleModalBuy}>
                        Buy Now
                    </button>
                </div>
            </Modal>

            <CartPanel 
                isOpen={isCartOpen}
                onClose={() => setIsCartOpen(false)}
                cartItems={cartItems}
                onRemoveItem={handleRemoveFromCart}
            />

            {isAnimating && <div className="cart-animation">Item added to cart!</div>}
        </div>
    );
}


export default ChatPage;